import * as React from "react";
import styled from "styled-components";

import { ApplicationOnboardedUsers } from "../../services/users";
import { Table } from "@tanstack/react-table";
import Button from "@ingka/button";
import arrowLeft from "@ingka/ssr-icon/paths/arrow-left";
import arrowLeftToLine from "@ingka/ssr-icon/paths/arrow-left-to-line";
import arrowRight from "@ingka/ssr-icon/paths/arrow-right";
import arrowRightToLine from "@ingka/ssr-icon/paths/arrow-right-to-line";
import Text from "@ingka/text";
import Select, { Option } from "@ingka/select";
import { fontSize75 } from "@ingka/variables/design-tokens";

type PageSize = 10 | 20 | 30;
const allowedPageSizes: PageSize[] = [10, 20, 30];

const StyledPagination = styled.nav`
	display: flex;
	flex-direction: column;
	align-items: center;

	nav {
		display: flex;
		justify-content: center;
		width: 100%;
	}

	.pagination-controls {
		display: flex;
		list-style: none;
		padding: 0;
		margin: 0;
		gap: 0.5rem;
	}

	.select select {
		padding: 0;
		min-height: 2rem;
		padding-inline-start: 0.5rem;
		padding-inline-end: 2.5rem;
		font-size: ${fontSize75};
	}

	.page-info {
		display: flex;
		align-items: center;
	}
`;

const UserPagiantion: React.FC<{ data: Table<ApplicationOnboardedUsers> }> = ({ data }) => {
	return (
		<StyledPagination aria-label="Pagination">
			<div className="pagination-controls">
				<Button
					iconOnly
					size="xsmall"
					type="tertiary"
					onClick={() => data.setPageIndex(0)}
					disabled={!data.getCanPreviousPage()}
					ssrIcon={arrowLeftToLine}
					aria-label="Go to first page"
				/>
				<Button
					size="xsmall"
					text="Previous"
					type="tertiary"
					onClick={() => data.previousPage()}
					disabled={!data.getCanPreviousPage()}
					ssrIcon={arrowLeft}
					aria-label="Go to previous page"
				/>
				<div className="page-info" aria-live="polite">
					<Text
						tagName="span"
						textStyle="Body.M"
						aria-label={`Page ${data.getState().pagination.pageIndex + 1} of ${data.getPageCount()}`}
					>
						Page&nbsp;<strong aria-hidden="true">{data.getState().pagination.pageIndex + 1}</strong> of&nbsp;
						<strong aria-hidden="true">{data.getPageCount()}</strong>
					</Text>
				</div>

				<Button
					size="xsmall"
					text="Next"
					type="tertiary"
					iconPosition="trailing"
					onClick={() => data.nextPage()}
					disabled={!data.getCanNextPage()}
					ssrIcon={arrowRight}
					aria-label="Go to next page"
				/>
				<Button
					iconOnly
					size="xsmall"
					type="tertiary"
					onClick={() => data.setPageIndex(data.getPageCount() - 1)}
					disabled={!data.getCanNextPage()}
					ssrIcon={arrowRightToLine}
					aria-label="Go to last page"
				/>
				<div className="page-size-control">
					<Select
						id="pageSizeSelect"
						aria-label="Rows per page"
						value={data.getState().pagination.pageSize.toString()}
						hintText=""
						onChange={(e) => {
							data.setPageSize(Number(e.target.value));
						}}
					>
						{allowedPageSizes.map((pageSize) => (
							<Option key={pageSize} value={pageSize.toString()} name={`Show ${pageSize}`} />
						))}
					</Select>
				</div>
			</div>
		</StyledPagination>
	);
};

export default UserPagiantion;
